import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { organizationSelector } from '@rsos/reduxDataStore/src/features/selectors/organizationSelector';
import {
  useGetHistoricConversationsQuery,
  useDownloadConversationsMutation,
  THIRTY_DAYS,
  type Conversation,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import onSAMLrequest from '@rsos/utils/onSAMLrequest';
import { notifyError } from '@rsos/utils/toastUtils';
import { persistor } from '../../../app/store';
import useAvailableSessionTypes from '../../../hooks/useAvailableSessionTypes';
import { type AppState } from '../../../types';
import { dayEnd, dayStart } from '../../../utils/helpers';
import styles from './ConversationHistory.module.scss';
import ConversationTable from './ConversationTable/ConversationTable';
import Toolbar from './Toolbar';

const SUPPORT_SERVICE = 'support';

// Stable reference so an empty/skipped query result doesn't change identity
// each render, which would retrigger the checkedList effect indefinitely.
const EMPTY_CONVERSATIONS: Conversation[] = [];

const ConversationHistory = () => {
  const dispatch = useDispatch();
  const now = useMemo(() => Date.now(), []);
  const availableSessionTypes = useAvailableSessionTypes();

  const [startDate, setStartDate] = useState<Date>(
    () => new Date(now - THIRTY_DAYS),
  );
  const [endDate, setEndDate] = useState<Date>(() => new Date(now));
  const [selectedTypes, setSelectedTypes] = useState<string[]>(() =>
    availableSessionTypes[0]?.value ? [availableSessionTypes[0].value] : [],
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [checkedList, setCheckedList] = useState<Set<string>>(new Set());
  const [isSortReversed, setIsSortReversed] = useState(false);

  const orgName = useSelector((state: AppState) =>
    organizationSelector(state),
  )?.name;
  const psapsDisplayName = useSelector(
    (state: AppState) => state.psaps.currentPSAP?.display_name,
  );
  const isLoadingSAML = useSelector(
    (state: AppState) => state.sinatra.auth.loadingSAML,
  );
  const handleSupportRequest = () =>
    onSAMLrequest(SUPPORT_SERVICE, dispatch, isLoadingSAML, persistor);

  const startTime = useMemo(() => dayStart(startDate), [startDate]);
  const endTime = useMemo(() => dayEnd(endDate), [endDate]);
  const sessionTypesParam = selectedTypes.join(',');

  const {
    data: conversations = EMPTY_CONVERSATIONS,
    isFetching: isLoading,
    isError,
  } = useGetHistoricConversationsQuery(
    {
      orgName,
      startTime,
      endTime,
      sessionTypes: sessionTypesParam,
      // always show active and inactive conversations
      // Interagency conversations are always active for example
      status: 'active,inactive',
    },
    {
      skip: !orgName || selectedTypes.length === 0,
      refetchOnMountOrArgChange: true,
    },
  );

  const [
    triggerDownload,
    { isLoading: isDownloading, isError: isDownloadError },
  ] = useDownloadConversationsMutation();

  useEffect(() => {
    if (isDownloadError) {
      notifyError('Failed to download conversations.');
    }
  }, [isDownloadError]);

  const filteredData = useMemo(
    () =>
      searchTerm
        ? conversations.filter(d => d.phone_number?.includes(searchTerm))
        : conversations,
    [conversations, searchTerm],
  );

  const displayList = useMemo(
    () => (isSortReversed ? filteredData.toReversed() : filteredData),
    [filteredData, isSortReversed],
  );

  useEffect(() => {
    setCheckedList(prev => {
      const next = new Set<string>();

      filteredData.forEach(d => {
        if (prev.has(d.conversation_id)) {
          next.add(d.conversation_id);
        }
      });

      return next;
    });
  }, [filteredData]);

  const checkedCount = checkedList.size;
  const isSelectedAllChecked =
    checkedCount > 0 && checkedCount === filteredData.length;
  const hasAnyChecked = checkedCount > 0;

  const toggleSelectAll = () => {
    isSelectedAllChecked
      ? setCheckedList(new Set())
      : setCheckedList(new Set(filteredData.map(d => d.conversation_id)));
  };

  const handleCheck = (id: string) => {
    setCheckedList(prev => {
      const next = new Set(prev);

      next.has(id) ? next.delete(id) : next.add(id);

      return next;
    });
  };

  const handleDownload = useCallback(
    (conversationIds: string[]) => {
      triggerDownload({ conversationIds, orgName, psapsDisplayName });
    },
    [orgName, psapsDisplayName, triggerDownload],
  );

  const toggleType = (value: string) => {
    setSelectedTypes(prev => {
      if (prev.includes(value)) {
        return prev.length === 1 ? prev : prev.filter(t => t !== value); // keep at least one selected
      }

      return [...prev, value];
    });
  };

  return (
    <div className={styles.container}>
      <Toolbar
        availableSessionTypes={availableSessionTypes}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        startDate={startDate}
        endDate={endDate}
        now={now}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        selectedTypes={selectedTypes}
        onToggleType={toggleType}
        filteredConversationIds={filteredData.map(d => d.conversation_id)}
        hasAnyChecked={hasAnyChecked}
        checkedIds={Array.from(checkedList)}
        onDownload={handleDownload}
        isDownloading={isDownloading}
      />

      <p className={styles.description}>
        All conversations are available to download as a CSV file. Please reach
        out to{' '}
        <button className={styles.supportLink} onClick={handleSupportRequest}>
          support
        </button>{' '}
        if you have any questions.
      </p>

      <ConversationTable
        displayList={displayList}
        isSortReversed={isSortReversed}
        onSort={() => setIsSortReversed(prev => !prev)}
        checkedList={checkedList}
        isSelectedAllChecked={isSelectedAllChecked}
        onToggleSelectAll={toggleSelectAll}
        onCheck={handleCheck}
        onDownload={handleDownload}
        isDownloading={isDownloading}
        isLoading={isLoading}
        isError={isError}
      />
    </div>
  );
};

export default ConversationHistory;
